import { driver } from 'driver.js'
import { nextTick } from 'vue'

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// Attend qu'un élément soit réellement présent dans le DOM (poll ~100ms, plafonné) avant
// de continuer. Utilisé après une navigation de page : s'appuyer uniquement sur le
// "waitForElement" de driver.js est risqué, car passé son délai il surligne à la place un
// élément factice invisible plutôt que d'attendre plus longtemps ou de sauter l'étape.
const waitForCondition = async (check, maxAttempts = 30) => {
  let attempts = 0
  while (!check() && attempts < maxAttempts) {
    await wait(100)
    attempts++
  }
  await nextTick()
}

const firstCard = () => document.querySelector('[data-cy="category-card"]')
const firstCardNavDot2 = () => document.querySelector('[data-cy="card-nav-dot-2"]')

const categoriesNavLink = () => document.querySelector('a[aria-label="Catégories"]')
const firstCategorieCard = () => document.querySelector('[data-cy="categorie-card"]')
const firstCategorieViewBtn = () =>
  document.querySelector('[data-cy="categorie-card"] [data-cy="categorie-view"]')
const firstDepenseItemOrEmpty = () =>
  document.querySelector('[data-cy="depense-item"]') ||
  document.querySelector('[data-cy="depenses-empty"]')

// Attend l'apparition réelle du widget du tableau de bord (la transition de route
// <transition mode="out-in"> retarde le montage de la page après un router.push) puis
// la fin du chargement des catégories/dépenses, avant d'évaluer les étapes du tour.
async function waitForDashboardReady(categorieDepenseStore) {
  let attempts = 0
  while (!document.querySelector('[data-cy="widget-total-depense"]') && attempts < 50) {
    await wait(100)
    attempts++
  }

  attempts = 0
  while (categorieDepenseStore?.isLoading && attempts < 40) {
    await wait(100)
    attempts++
  }

  await nextTick()
}

export default async function startOnboardingTour({
  modalAddContentStore,
  categorieDepenseStore,
  router
}) {
  await waitForDashboardReady(categorieDepenseStore)

  const hasCard = !!firstCard()

  const closeAddModal = async () => {
    modalAddContentStore.show = false
    await nextTick()
  }

  const openAddModal = async () => {
    modalAddContentStore.show = true
    await nextTick()
    await wait(250)
  }

  const switchToTab = async (tabId) => {
    const tabButton = document.getElementById(tabId)
    if (tabButton) tabButton.click()
    await nextTick()
    await wait(250)
  }

  const steps = [
    {
      element: '[data-cy="widget-total-depense"]',
      popover: {
        title: "Votre vue d'ensemble",
        description: 'Ce bloc affiche diverses informations globales sur vos dépenses.',
        side: 'bottom',
        align: 'start'
      }
    }
  ]

  if (hasCard) {
    steps.push(
      {
        element: firstCard,
        popover: {
          title: 'Vos catégories de dépenses',
          description: 'Chaque bloc représente une catégorie et affiche vos 3 dernières dépenses.',
          side: 'top',
          align: 'start',
          onNextClick: async (element, step, opts) => {
            const dot2 = firstCardNavDot2()
            if (dot2) dot2.click()
            await nextTick()
            opts.driver.moveNext()
          }
        }
      },
      {
        element: firstCard,
        popover: {
          title: 'Vue jauge',
          description:
            'En cliquant sur le second point en haut à droite, vous basculez vers une jauge qui compare vos dépenses à la limite de la catégorie.',
          side: 'top',
          align: 'start'
        }
      }
    )
  }

  steps.push(
    {
      element: '#btn-add-new-depense',
      popover: {
        title: 'Ajouter du contenu',
        description: "Ce bouton vous permet à tout moment d'ajouter une dépense ou une catégorie.",
        side: 'left',
        align: 'start',
        onNextClick: async (element, step, opts) => {
          await openAddModal()
          opts.driver.moveNext()
        }
      }
    },
    {
      element: '.glass-segmented-control',
      popover: {
        title: 'Type de contenu',
        description:
          'Ce sélecteur permet de choisir ce que vous souhaitez ajouter : une dépense, une catégorie, ou plusieurs dépenses en une fois.',
        side: 'bottom',
        align: 'start',
        // La modale vient de s'ouvrir : revenir en arrière la laisserait ouverte devant le bouton "Ajouter".
        showButtons: ['next', 'close'],
        onNextClick: async (element, step, opts) => {
          await switchToTab('tab-categorie')
          opts.driver.moveNext()
        }
      }
    },
    {
      element: '[data-cy="form-categorie"]',
      popover: {
        title: 'Créer une catégorie',
        description:
          'Donnez-lui un nom, définissez une limite mensuelle, puis choisissez une couleur et une icône pour la personnaliser.',
        side: 'bottom',
        align: 'start',
        onNextClick: async (element, step, opts) => {
          await closeAddModal()
          await openAddModal()
          opts.driver.moveNext()
        }
      }
    },
    {
      element: '#tab-depense',
      popover: {
        title: 'Ajouter une dépense',
        description: 'Cet onglet vous permet de saisir une nouvelle dépense.',
        side: 'bottom',
        align: 'start',
        // La modale vient de se rouvrir sur l'onglet "dépense" : revenir en arrière casserait le ciblage.
        showButtons: ['next', 'close']
      }
    },
    {
      element: '[data-cy="form-depense"]',
      popover: {
        title: 'Ajouter une dépense',
        description: 'Indiquez le nom, le montant et la catégorie de votre dépense.',
        side: 'bottom',
        align: 'start'
      }
    },
    {
      element: 'label[for="type-depense-recurrente"]',
      popover: {
        title: 'Dépense récurrente',
        description:
          "Si cette dépense revient régulièrement, activez ce mode pour qu'elle soit ajoutée automatiquement selon la fréquence choisie.",
        side: 'bottom',
        align: 'start',
        onNextClick: async (element, step, opts) => {
          const label = document.querySelector('label[for="type-depense-recurrente"]')
          if (label) label.click()
          await nextTick()
          opts.driver.moveNext()
        }
      }
    },
    {
      element: '[data-cy="depense-recurrence-section"]',
      popover: {
        title: 'Fréquence de la récurrence',
        description:
          'Choisissez la fréquence : journalière, hebdomadaire, mensuelle ou annuelle. Vous retrouverez vos dépenses récurrentes dans la partie "Récurrences".',
        side: 'bottom',
        align: 'start'
      }
    },
    {
      element: '#tab-multiple',
      popover: {
        title: 'Dépenses multiples',
        description: 'Vous pouvez aussi ajouter plusieurs dépenses en une seule fois.',
        side: 'bottom',
        align: 'start',
        onNextClick: async (element, step, opts) => {
          await switchToTab('tab-multiple')
          opts.driver.moveNext()
        }
      }
    },
    {
      element: '[data-cy="form-multiple"]',
      popover: {
        title: 'Dépenses multiples',
        description:
          'Saisissez une dépense par ligne, au format "nom catégorie montant". Un aperçu vous indique les lignes valides et celles à corriger.',
        side: 'bottom',
        align: 'start',
        onNextClick: async (element, step, opts) => {
          await closeAddModal()
          opts.driver.moveNext()
        }
      }
    }
  )

  if (hasCard) {
    steps.push(
      {
        element: categoriesNavLink,
        popover: {
          title: 'Vos catégories',
          description: 'Rendez-vous dans cet onglet pour retrouver toutes vos catégories.',
          side: 'bottom',
          align: 'start',
          // La modale vient de se fermer : revenir en arrière casserait le ciblage de l'étape précédente.
          showButtons: ['next', 'close'],
          onNextClick: async (element, step, opts) => {
            const link = categoriesNavLink()
            if (link) link.click()
            await waitForCondition(
              () => firstCategorieCard() || document.querySelector('[data-cy="categories-empty"]')
            )
            opts.driver.moveNext()
          }
        }
      },
      {
        element: firstCategorieCard,
        popover: {
          title: 'Vos catégories',
          description: 'Prenons votre première catégorie pour vous montrer son détail.',
          side: 'bottom',
          align: 'start'
        }
      },
      {
        element: firstCategorieViewBtn,
        popover: {
          title: 'Voir le détail',
          description: "Cliquez sur l'icône œil pour accéder au détail de la catégorie.",
          side: 'left',
          align: 'start',
          onNextClick: async (element, step, opts) => {
            const btn = firstCategorieViewBtn()
            if (btn) btn.click()
            await waitForCondition(() =>
              document.querySelector('[data-cy="categorie-detail-title"]')
            )
            opts.driver.moveNext()
          }
        }
      },
      {
        element: '[data-cy="categorie-detail-title"]',
        popover: {
          title: 'Détail de la catégorie',
          description:
            'Cette page présente le détail des dépenses de la catégorie : statistiques, filtres et historique complet.',
          side: 'bottom',
          align: 'start',
          // On vient de naviguer vers une nouvelle page : revenir en arrière casserait le ciblage.
          showButtons: ['next', 'close']
        }
      },
      {
        element: '[data-cy="categorie-stats"]',
        popover: {
          title: 'Statistiques',
          description:
            'Retrouvez ici le total dépensé, le budget restant, la limite fixée et le nombre de transactions.',
          side: 'bottom',
          align: 'start'
        }
      },
      {
        element: '[data-cy="details-filters-toggle"]',
        popover: {
          title: 'Filtres',
          description:
            "Utilisez ce bouton pour filtrer l'historique des dépenses par nom ou par période.",
          side: 'bottom',
          align: 'start'
        }
      },
      {
        element: firstDepenseItemOrEmpty,
        popover: {
          title: 'Historique des dépenses',
          description: 'Retrouvez ici la liste complète des dépenses de cette catégorie.',
          side: 'top',
          align: 'start'
        }
      }
    )
  } else {
    steps.push({
      popover: {
        title: 'Détails de la catégorie',
        description:
          "Une fois vos catégories créées, rendez-vous dans l'onglet \"Catégories\" et cliquez sur l'icône œil d'une catégorie pour afficher le détail de ses dépenses, ses statistiques et son historique.",
        showButtons: ['next', 'close']
      }
    })
  }

  steps.push({
    popover: {
      title: 'Vous êtes prêt',
      description: 'Profitez bien de Save My Money.',
      showButtons: ['next', 'close'],
      onNextClick: (element, step, opts) => {
        opts.driver.destroy()
        if (router.currentRoute.value.name !== 'home') {
          router.push({ name: 'home' })
        }
      }
    }
  })

  const tourDriver = driver({
    showProgress: true,
    smoothScroll: true,
    allowClose: true,
    overlayOpacity: 0.6,
    waitForElement: 2500,
    popoverClass: 'glass-tour-popover',
    nextBtnText: 'Suivant',
    prevBtnText: 'Précédent',
    doneBtnText: 'Terminer',
    progressText: '{{current}} / {{total}}',
    onDestroyed: () => {
      modalAddContentStore.show = false
    },
    steps
  })

  tourDriver.drive()

  return tourDriver
}
